import { useRef, useState } from "react";
import EntityManager from "@/components/EntityManager";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Printer, Wrench, Plus, Download, Paperclip, Eye, X } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";
import { BRANCHES } from "@/lib/branches";
import { inr, fmtDate, today } from "@/lib/format";
import { printAssetRegister, printMaintenanceReport, printDepreciationSchedule, printScrapValueReport } from "@/lib/print";

const CATEGORIES = ["Office Equipment", "Furniture", "Computer / IT", "Electrical", "Vehicle Accessory", "Tools", "Other"];
const CONDITIONS = ["Good", "Needs Repair", "Scrap"];
const STATUSES = ["In Use", "In Storage", "Under Repair", "Disposed"];

const condCls = { Good: "bg-emerald-50 text-emerald-700", "Needs Repair": "bg-amber-50 text-amber-700", Scrap: "bg-red-50 text-red-700" };

const csvEsc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
const downloadCsv = (name, lines) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
  a.download = name;
  a.click();
};
const bookValue = (r) => {
  const v = Number(r.purchase_value) || 0;
  const rate = Number(r.depreciation_rate) || 0;
  if (!rate || !r.purchase_date) return v;
  const yrs = Math.max(0, (Date.now() - new Date(String(r.purchase_date).slice(0, 10)).getTime()) / (365.25 * 86400000));
  return Math.max(0, v * Math.pow(1 - rate / 100, yrs));
};

const exportAssetExcel = (rows) => {
  const lines = ["Sl,Branch,Asset Name,Asset Code,Category,Purchase Date,Purchase Value,Dep % / yr,Book Value,Condition,Status,Repairs,Maintenance Cost,Notes"];
  rows
    .slice()
    .sort((a, b) => String(a.branch || "").localeCompare(String(b.branch || "")) || String(a.name || "").localeCompare(String(b.name || "")))
    .forEach((r, i) => {
      const reps = r.repairs || [];
      lines.push([i + 1, csvEsc(r.branch || "Chennai"), csvEsc(r.name), csvEsc(r.asset_code), csvEsc(r.category), csvEsc(r.purchase_date ? fmtDate(r.purchase_date) : ""), Number(r.purchase_value) || 0, Number(r.depreciation_rate) || 0, Math.round(bookValue(r)), csvEsc(r.condition || "Good"), csvEsc(r.status || "In Use"), reps.length, reps.reduce((a, x) => a + (Number(x.cost) || 0), 0), csvEsc(r.notes)].join(","));
    });
  downloadCsv("asset-register.csv", lines);
};
const exportMaintenanceExcel = (rows) => {
  const lines = ["Branch,Asset Name,Asset Code,Category,Purchase Value,Repair Date,Repair Note,Repair Cost"];
  rows
    .filter((r) => (r.repairs || []).length)
    .sort((a, b) => String(a.branch || "").localeCompare(String(b.branch || "")))
    .forEach((r) => {
      (r.repairs || [])
        .slice()
        .sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")))
        .forEach((x) => {
          lines.push([csvEsc(r.branch || "Chennai"), csvEsc(r.name), csvEsc(r.asset_code), csvEsc(r.category), Number(r.purchase_value) || 0, csvEsc(x.date ? fmtDate(x.date) : ""), csvEsc(x.note), Number(x.cost) || 0].join(","));
        });
    });
  if (lines.length === 1) return false;
  downloadCsv("asset-maintenance-report.csv", lines);
  return true;
};

export default function AssetBook() {
  const loadRef = useRef(null);
  const billFileRef = useRef(null);
  const [billFor, setBillFor] = useState(null);
  const [billBusy, setBillBusy] = useState("");
  const [repairAsset, setRepairAsset] = useState(null);
  const [rform, setRform] = useState({ date: today(), cost: 0, note: "" });
  const [saving, setSaving] = useState(false);

  const openRepairs = (asset) => {
    setRepairAsset(asset);
    setRform({ date: today(), cost: 0, note: "" });
  };
  const addRepair = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/assets/${repairAsset.id}/repairs`, rform);
      setRepairAsset((p) => ({ ...p, condition: "Good", repairs: [...(p.repairs || []), res.data.repair] }));
      setRform({ date: today(), cost: 0, note: "" });
      toast.success(`Repair logged for ${repairAsset.name} — condition set to Good`);
      loadRef.current && loadRef.current();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  const history = (repairAsset?.repairs || []).slice().sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));

  return (
    <>
    <input
      ref={billFileRef}
      type="file"
      accept=".jpg,.jpeg,.png,.webp,.pdf"
      className="hidden"
      onChange={async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f || !billFor) return;
        setBillBusy(billFor.id);
        try {
          const fd = new FormData();
          fd.append("file", f);
          await api.post(`/assets/${billFor.id}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          toast.success(`Bill copy attached to "${billFor.name}"`);
          loadRef.current && loadRef.current();
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail));
        } finally {
          setBillBusy("");
          setBillFor(null);
        }
      }}
      data-testid="asset-bill-file-input"
    />
    <EntityManager
      title="NTC Asset Book"
      subtitle="Track office assets across all branches — furniture, computers, equipment and more."
      endpoint="assets"
      entityName="Asset"
      headerExtra={(rows, displayRows, filterVals, load) => {
        loadRef.current = load;
        return (
          <>
            <Button
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              disabled={!rows.length}
              onClick={() => { if (!printMaintenanceReport(rows)) toast.info("No repairs logged yet — nothing to report."); }}
              data-testid="print-maintenance-report-btn"
            >
              <Wrench className="mr-2 h-4 w-4" /> Maintenance Report
            </Button>
            <Button variant="outline" disabled={!rows.length} onClick={() => printAssetRegister(rows)} data-testid="print-asset-register-btn">
              <Printer className="mr-2 h-4 w-4" /> Print Register
            </Button>
            <Button variant="outline" disabled={!rows.length} onClick={() => exportAssetExcel(rows)} data-testid="asset-excel-btn">
              <Download className="mr-2 h-4 w-4" /> Register Excel
            </Button>
            <Button
              variant="outline"
              disabled={!rows.length}
              onClick={() => { if (!exportMaintenanceExcel(rows)) toast.info("No repairs logged yet — nothing to export."); }}
              data-testid="maintenance-excel-btn"
            >
              <Download className="mr-2 h-4 w-4" /> Maintenance Excel
            </Button>
            <Button
              variant="outline"
              disabled={!rows.some((r) => Number(r.depreciation_rate) > 0 && r.purchase_date)}
              onClick={() => printDepreciationSchedule(rows)}
              title="Year-by-year WDV depreciation schedule per asset for the CA's file"
              data-testid="dep-schedule-btn"
            >
              <Printer className="mr-2 h-4 w-4" /> Dep Schedule
            </Button>
            <Button
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50"
              disabled={!rows.length}
              onClick={() => printScrapValueReport(rows)}
              title="Assets whose book value fell below 10% of purchase value"
              data-testid="scrap-report-btn"
            >
              <Printer className="mr-2 h-4 w-4" /> Scrap Report
            </Button>
          </>
        );
      }}
      filters={[
        {
          key: "q",
          label: "Search",
          type: "text",
          placeholder: "Asset / code / category / notes",
          match: (r, v) => {
            const q = String(v || "").trim().toLowerCase();
            if (!q) return true;
            return [r.name, r.asset_code, r.category, r.branch, r.status, r.condition, r.notes].some((x) => String(x || "").toLowerCase().includes(q));
          },
        },
        { key: "branch", label: "Branch", type: "select", options: ["All Branches", ...BRANCHES], match: (r, v) => !v || v === "All Branches" || (r.branch || "Chennai") === v },
        { key: "category", label: "Category", type: "select", options: ["All", ...CATEGORIES], match: (r, v) => !v || v === "All" || r.category === v },
        { key: "status", label: "Status", type: "select", options: ["All", ...STATUSES], match: (r, v) => !v || v === "All" || (r.status || "In Use") === v },
      ]}
      defaults={{ name: "", asset_code: "", category: "Office Equipment", branch: "Chennai", purchase_date: "", purchase_value: 0, condition: "Good", status: "In Use", notes: "" }}
      fields={[
        { key: "name", label: "Asset Name", type: "text", placeholder: "e.g. HP LaserJet Printer" },
        { key: "asset_code", label: "Asset Code", type: "text", placeholder: "e.g. AST-001" },
        { key: "category", label: "Category (pick or type your own)", type: "text", placeholder: "e.g. Office Equipment", suggestions: CATEGORIES },
        { key: "branch", label: "Branch", type: "select", options: BRANCHES },
        { key: "purchase_date", label: "Purchase Date", type: "date" },
        { key: "purchase_value", label: "Purchase Value (₹)", type: "number" },
        { key: "depreciation_rate", label: "Depreciation % per year (WDV)", type: "number", placeholder: "e.g. 15" },
        { key: "condition", label: "Condition", type: "select", options: CONDITIONS },
        { key: "status", label: "Status", type: "select", options: STATUSES },
        { key: "notes", label: "Notes", type: "textarea", placeholder: "Serial no., warranty, remarks…" },
      ]}
      columns={[
        { key: "sl_no", label: "Sl No.", render: (r, i) => i + 1 },
        { key: "branch", label: "Branch", render: (r) => r.branch || "Chennai" },
        { key: "name", label: "Asset Name" },
        { key: "asset_code", label: "Asset Code", render: (r) => r.asset_code || "—" },
        { key: "category", label: "Category" },
        { key: "purchase_date", label: "Purchase Date", render: (r) => (r.purchase_date ? fmtDate(r.purchase_date) : "—") },
        { key: "purchase_value", label: "Value", render: (r) => inr(Number(r.purchase_value) || 0) },
        {
          key: "_book",
          label: "Book Value",
          render: (r) => {
            const bv = bookValue(r);
            const dep = Number(r.depreciation_rate) || 0;
            return (
              <span title={dep ? `${dep}% WDV per year since purchase` : "No depreciation rate set"} data-testid="asset-book-value">
                <b>{inr(Math.round(bv))}</b>{dep ? <span className="ml-1 text-[10px] text-muted-foreground">@{dep}%</span> : null}
              </span>
            );
          },
        },
        {
          key: "condition",
          label: "Condition",
          render: (r) => (
            <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${condCls[r.condition] || "bg-slate-100 text-slate-600"}`} data-testid="asset-condition-chip">
              {r.condition || "Good"}
            </span>
          ),
        },
        { key: "status", label: "Status", render: (r) => r.status || "In Use" },
        {
          key: "_repairs",
          label: "Repairs",
          render: (r) => {
            const list = r.repairs || [];
            const total = list.reduce((a, x) => a + (Number(x.cost) || 0), 0);
            return (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openRepairs(r); }}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${list.length ? "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100" : "border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100"}`}
                title={list.length ? `${list.length} repair(s) · ${inr(total)} — open history` : "Log a repair"}
                data-testid="asset-repairs-chip"
              >
                <Wrench className="h-3 w-3" />
                {list.length ? `${list.length} · ${inr(total)}` : "Log"}
              </button>
            );
          },
        },
        {
          key: "_bill",
          label: "Bill Copy",
          render: (r) =>
            r.attachment_path ? (
              <span className="inline-flex items-center gap-1">
                <a
                  href={`${API}/files/${r.attachment_path}?auth=${localStorage.getItem("ntc_token")}`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700 hover:bg-emerald-100"
                  title={r.attachment_filename || "View repair bill copy"}
                  data-testid="asset-bill-view"
                >
                  <Eye className="h-3 w-3" /> View
                </a>
                <button
                  type="button"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!window.confirm(`Remove bill copy from "${r.name}"?`)) return;
                    setBillBusy(r.id);
                    try {
                      await api.delete(`/assets/${r.id}/attachment`);
                      toast.success("Bill copy removed");
                      loadRef.current && loadRef.current();
                    } catch (err) {
                      toast.error(formatApiError(err.response?.data?.detail));
                    } finally {
                      setBillBusy("");
                    }
                  }}
                  disabled={billBusy === r.id}
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-400 hover:bg-red-50 hover:text-red-600"
                  title="Remove bill copy"
                  data-testid="asset-bill-remove"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setBillFor(r);
                  billFileRef.current && billFileRef.current.click();
                }}
                disabled={billBusy === r.id}
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-bold text-slate-500 hover:bg-slate-100"
                title="Attach repair bill copy (JPG/PNG/PDF)"
                data-testid="asset-bill-attach"
              >
                <Paperclip className="h-3 w-3" /> {billBusy === r.id ? "Uploading…" : "Attach"}
              </button>
            ),
        },
      ]}
    />
    <Dialog open={!!repairAsset} onOpenChange={(o) => !o && setRepairAsset(null)}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[560px]" data-testid="asset-repair-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {repairAsset?.name} {repairAsset?.asset_code ? `(${repairAsset.asset_code})` : ""} — Repair History
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-[130px_1fr_auto] items-end gap-2">
          <div className="grid gap-1">
            <Label className="text-xs">Repair Date</Label>
            <Input type="date" className="h-9" value={rform.date} onChange={(e) => setRform((f) => ({ ...f, date: e.target.value }))} data-testid="repair-date-input" />
          </div>
          <div className="grid grid-cols-[110px_1fr] gap-2">
            <div className="grid gap-1">
              <Label className="text-xs">Cost (₹)</Label>
              <Input type="number" className="h-9" value={rform.cost} onChange={(e) => setRform((f) => ({ ...f, cost: Number(e.target.value) }))} data-testid="repair-cost-input" />
            </div>
            <div className="grid gap-1">
              <Label className="text-xs">What was repaired?</Label>
              <Input className="h-9" placeholder="e.g. Compressor replaced" value={rform.note} onChange={(e) => setRform((f) => ({ ...f, note: e.target.value }))} data-testid="repair-note-input" />
            </div>
          </div>
          <Button className="h-9" onClick={addRepair} disabled={saving || !rform.date} data-testid="repair-add-btn">
            <Plus className="mr-1 h-4 w-4" /> Add
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">Logging a repair automatically sets the asset's condition back to <b>Good</b>.</p>
        {history.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No repairs logged yet.</p>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  {["Date", "Repair", "Cost"].map((h) => (
                    <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((x, i) => (
                  <tr key={i} className="border-t" data-testid="repair-history-row">
                    <td className="px-3 py-1.5 whitespace-nowrap">{x.date ? fmtDate(x.date) : "—"}</td>
                    <td className="px-3 py-1.5">{x.note || "—"}</td>
                    <td className="px-3 py-1.5 font-semibold">{inr(Number(x.cost) || 0)}</td>
                  </tr>
                ))}
                <tr className="border-t bg-slate-50 font-bold">
                  <td className="px-3 py-1.5" colSpan={2}>TOTAL MAINTENANCE COST</td>
                  <td className="px-3 py-1.5" data-testid="repair-total">{inr(history.reduce((a, x) => a + (Number(x.cost) || 0), 0))}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
